import { ChangeEvent, FC, useEffect, useRef, useState } from 'react';
import { Box, Space, Accordion, Radio, MultiSelect, Text, Title, Flex } from '@mantine/core';
import { useForm } from '@mantine/form';
import { TextInput, Select, RadioButton, Checkbox, AutomatedAuditLoader } from '@common/components';
import { useTranslation } from 'react-i18next';
import { useAlert, useFormFocus } from '@common/hooks';
import { Button } from '@common/components';
import styles from './styles.module.scss';
import classNames from 'classnames';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  fetchAuditEngine,
  fetchLatestDashboard,
  fetchReportSettings,
  setDefault,
  updateReportSettings,
  useAppDispatch,
  useAppSelector,
} from 'src/store';
import { shallowEqual } from 'react-redux';
import { frequencyMap } from 'src/constants/automatedSettings';
import i18n from '@common/i18n/config';
import './style.css';

interface IScanPageProps {
  complianceLevel: string;
  engine: string;
  device: string;
  browser: string;
  criteria: string;
  websiteName: string;
}
const initialValues: IScanPageProps = {
  complianceLevel: 'compliance1',
  engine: 'Engine: WCAG-AA Ialabs 1',
  device: 'device1',
  browser: 'browser1',
  criteria: 'criteria1',
  websiteName: '',
};

interface ISelectData {
  value: string;
  label: string;
}

const SettingsPage: FC = () => {
  const { reportSettings, auditEngines, dashboardLoading, latestDashboard, settingsLoading, loading, error } =
    useAppSelector((state) => state.automatedReports, shallowEqual);
  const { tenantId } = useAppSelector((state) => state.user.data, shallowEqual);

  // const [t] = useTranslation('scanPage');
  const form = useForm({
    initialValues: initialValues,
  });
  const { setNeedFocus } = useFormFocus({ formErrors: form.errors });
  const errorRef = useRef<HTMLDivElement>(null);
  const { showAlert } = useAlert();
  const onSubmitHandler = async (values: IScanPageProps): Promise<void> => {
    updateSettings();
    if (!error && !settingsLoading) {
      showAlert({
        id: 'settingsUpdate',
        title: 'Settings Update',
        message: 'Settings has been updated successfully',
        variant: 'success',
        autoClose: 3000,
      });
    } else {
      showAlert({
        id: 'settingsUpdate',
        title: 'Settings Update',
        message: 'Failed to update settings',
        variant: 'error',
      });
    }
  };

  const onErrorHandler = (): void => {
    setNeedFocus(true);
  };

  function handleChange(e: any): void {
    // console.log('api not implemented yet.');
    console.log(e);
  }

  const compliance = ['compliance1', 'compliance2', 'compliance3', 'compliance4'];
  const engines = [
    'Engine: WCAG-AA Ialabs 1',
    'Engine: WCAG-AA Ialabs 2',
    'Engine: WCAG-AA Ialabs 3',
    'Engine: WCAG-AA Ialabs 4',
    'Engine: WCAG-AA Ialabs 5',
  ];
  const devices = ['device1', 'device2', 'device3', 'device4', 'device5'];
  const browsers = ['browser1', 'browser2', 'browser3', 'browser4', 'browser5'];
  const criterias = ['criteria1', 'criteria2', 'criteria3', 'criteria4', 'criteria5'];
  const [auditType, setAuditType] = useState('complete');
  const [pageToAudit, setPageToAudit] = useState('one');
  const [handelRedirection, setHandelRedirection] = useState('true');
  const { id } = useParams();
  useEffect(() => {
    if (!id) {
      return;
    }
    form.setFieldValue('websiteName', id);
  }, [id]);

  const [t] = useTranslation('automatedReports');
  const [c] = useTranslation('common');
  const dispatch = useAppDispatch();
  const [frequency, setFrequency] = useState<string | null>(
    reportSettings ? reportSettings.frequency : frequencyMap[0].value,
  );
  const [auditEngine, setAuditEngine] = useState<string | null>(reportSettings ? reportSettings.engine_id : null);
  const [notifications, setNotifications] = useState<boolean>(reportSettings ? reportSettings.notifications : false);
  const [auditEnginesData, setAuditEnginesData] = useState<ISelectData[]>([]);
  const [auditSpeed, setAuditSpeed] = useState<string>(reportSettings?.auditSpeed ? reportSettings.auditSpeed : 'high');
  const [redirect, setRedirect] = useState<boolean>(reportSettings?.redirect ? reportSettings.redirect : false);
  const speedOption = [
    {
      label: t('speeds.normalLabel'),
      value: t('speeds.normal'),
    },
    {
      label: t('speeds.HighLabel'),
      value: t('speeds.High'),
    },
  ];
  useEffect(() => {
    if (reportSettings) {
      setFrequency(reportSettings ? reportSettings.frequency : frequencyMap[0].value);
      setAuditEngine(reportSettings ? reportSettings.engine_id : null);
      setNotifications(reportSettings ? reportSettings.notifications : false);
      setAuditSpeed(reportSettings?.auditSpeed ? reportSettings.auditSpeed : 'high');
    }
  }, [reportSettings]);

  useEffect(() => {
    if (auditEngines) {
      const data: ISelectData[] = auditEngines.map((el) => ({
        label: `${el.name} ${el.compliance_level} ${el?.deviceName ? el.deviceName : ''} ${el.browserName}`,
        value: el.id,
      }));
      setAuditEnginesData(data);
    }
  }, [auditEngines]);

  const handleStatusChange = (value: string | null): void => {
    setFrequency(value);
  };

  const handleAuditEngine = (value: string | null): void => {
    setAuditEngine(value);
  };
  const handleAuditSpeed = (value: string | null): void => {
    setAuditSpeed(value ?? 'high');
  };

  const handleNativeStatusChange = (e: ChangeEvent<HTMLSelectElement>): void => {
    setFrequency(e.currentTarget.value);
  };

  const handleNativeAuditEngine = (e: ChangeEvent<HTMLSelectElement>): void => {
    setAuditEngine(e.currentTarget.value);
  };

  const updateSettings = (): void => {
    const data = Object.assign({}, reportSettings, { frequency, notifications, engine_id: auditEngine, auditSpeed,redirect });
    dispatch(updateReportSettings({ data, id }));
  };

  const [frequencyData, setFrequencyData] = useState<ISelectData[]>([]);

  useEffect(() => {
    const setValues = (): void => {
      const newStrings = frequencyMap.map((e) => ({ ...e, label: t(e.label) }));
      setFrequencyData(newStrings);
    };

    setValues();

    i18n.on('languageChanged', setValues);

    return () => {
      i18n.off('languageChanged', setValues);
    };
  }, []);
  const [searchParams, setSearchParams] = useSearchParams();
  const siteUUID = searchParams.get('siteUUID')

  useEffect(() => {
    if (tenantId) {
      dispatch(fetchReportSettings(id));
      dispatch(fetchLatestDashboard({ 'id':siteUUID })); 
      dispatch(fetchAuditEngine());
    }

    return () => {
      dispatch(setDefault());
    };
  }, [tenantId]);
  return (
    <>
      <Box id="main-wrapper" className={styles.main_wrapper}>
        <Title className={styles.title} mb={'18px'} order={1}>
          {t('settingTitle')}
        </Title>
        <Space h={'xl'} />
        {onSubmitHandler && (
          <form className={styles.form} onSubmit={form?.onSubmit(onSubmitHandler, onErrorHandler)}>
            {!reportSettings?.frequency && settingsLoading && (
              <Flex gap={'sm'} align={'center'}>
                <AutomatedAuditLoader />
                <span>{t('loading')}</span>
              </Flex>
            )}
            <Select
              variant="secondary"
              withBorder
              withIcon
              className={styles['dropdown']}
              aria-label={`${c('combobox')}`}
              label={`${t('defineScanFrequency')}`}
              value={frequency}
              data={frequencyData}
              onChange={handleStatusChange}
            />
            <Space h={'xl'} />
            {!auditEngine && settingsLoading && (
              <Flex gap={'sm'} align={'center'}>
                <AutomatedAuditLoader />
                <span>{t('loading')}</span>
              </Flex>
            )}
            <Select
              variant="secondary"
              withBorder
              withIcon
              aria-label={`${c('combobox')}`}
              className={styles['dropdown']}
              label={`${t('auditEngine')}`}
              value={auditEngine}
              data={auditEnginesData}
              onChange={handleAuditEngine}
            />
            <Space h={'xl'} />
            {!reportSettings?.auditSpeed && settingsLoading && (
              <Flex gap={'sm'} align={'center'}>
                <AutomatedAuditLoader />
                <span>{t('loading')}</span>
              </Flex>
            )}
            <Select
              variant="secondary"
              withBorder
              withIcon
              aria-label={`${c('combobox')}`}
              className={styles['dropdown']}
              label={`${t('auditSpeed')}`}
              value={auditSpeed}
              data={speedOption}
              onChange={handleAuditSpeed}
            />
            {/* <Space h={'xl'} />
                  <Radio.Group
                    name="pages-to-audit"
                    label="page to audit"
                    value={pageToAudit}
                    onChange={(val) => {
                      setPageToAudit(val);
                    }}
                    labelProps={{ style: { display: 'none' } }}>

                          <RadioButton
                            value="one"
                            label="Page to edit"
                          />
                          <RadioButton
                            value="many"
                            label="How many page to edit"
                          />
                  </Radio.Group>
                   <Space h={'md'} />

                      {pageToAudit == 'one' ?
                        <TextInput
                            className={styles.siteUriInput}
                            withAsterisk
                            label="Page to audit"
                            placeholder='Page to audit'
                            ariaLabel="Page to audit"
                            {...form.getInputProps('pagetoaudit')}
                        />
                      :
                      <TextInput
                          className={styles.siteUriInput}
                          withAsterisk
                          label="How many page to audit"
                          placeholder="e.g : 27"
                          ariaLabel="How many page to audit"
                          type='number'
                          {...form.getInputProps('pagetoaudit')}
                      />
                      }
                   <Space h={'md'} />



                   <Radio.Group
                    name="audit-type"
                    label="audit type"
                    value={auditType}
                    onChange={(val) => {
                      setAuditType(val);
                    }}
                    labelProps={{ style: { display: 'none' } }}>

                          <RadioButton
                            value="complete"
                            label="complete audit"
                          />
                          <RadioButton
                            value="sample"
                            label="sample audit"
                          />
                  </Radio.Group>
                  <Space h={'md'} />

                  {
                    auditType == 'sample' &&
                    <TextInput
                        className={styles.siteUriInput}
                        withAsterisk
                        label='adjustable % or fixed number of pages to audit '
                        placeholder= 'adjustable % or fixed number of pages to audit '
                        ariaLabel='enter  adjustable % or fixed number of pages to audit'
                        {...form.getInputProps('firstName')}
                    />

                  }

                  <Space h={'md'} />
                  <Radio.Group
                    name="handle-redirects "
                    label="handle redirects"
                    value={handelRedirection}
                    onChange={(val) => {
                      setHandelRedirection(val);
                    }}
                    labelProps={{ style: { display: 'inline-block',fontSize: '1rem',fontWeight: 'bold',color: '#212529',marginBottom:'10px'} }}>

                          <RadioButton
                            value="true"
                            label="Yes"
                          />
                          <RadioButton
                            value="false"
                            label="No"
                          />
                  </Radio.Group>

                  <Space h={'md'} />

                  <MultiSelect
                      className={classNames(styles['select-styling'], {
                      })}
                      label ={'compliance specification'}
                      aria-label={`compliance specification we are auditing against`}
                      data={compliance}
                      onChange={(e) => handleChange(e)}
                  />

                  <Space h={'md'} />

                  <Accordion className={styles.accordion}>
                        <Accordion.Item value= "Advanced Settings">
                            <Accordion.Control>
                                Advanced Settings
                            </Accordion.Control>
                            <Accordion.Panel>
                                <Select
                                    className={classNames(styles['select-styling'], {
                                    })}
                                    label ={`Audit engine`}
                                    variant="secondary"
                                    withIcon
                                    aria-label={`Audit engine to used`}
                                    value={form.values.engine}
                                    data={engines}
                                    onChange={(e) => handleChange(e)}
                                />

                                <Space h={'md'} />

                                    <Select
                                    className={classNames(styles['select-styling'], {
                                    })}
                                    label ={`Device`}
                                    variant="secondary"
                                    withIcon
                                    aria-label={`Device to used`}
                                    value={form.values.device}
                                    data={devices}
                                    onChange={(e) => handleChange(e)}
                                />

                                <Space h={'md'} />

                                <Select
                                className={classNames(styles['select-styling'], {
                                })}
                                label ={`Browser`}
                                variant="secondary"
                                withIcon
                                aria-label={`Browser to used`}
                                value={form.values.browser}
                                data={browsers}
                                onChange={(e) => handleChange(e)}
                                />
                            </Accordion.Panel>
                        </Accordion.Item>
                    </Accordion>


                    <Space h={'md'} />

                    <Select
                        label ={`Check by criteria`}
                        variant="secondary"
                        withIcon
                        aria-label={`Check by criteria`}
                        value={form.values.criteria}
                        data={criterias}
                        onChange={(e) => handleChange(e)}
                        className={styles['dropdown']}
                    />
                    <Space h={'md'} />

                    <TextInput
                        className={styles.siteUriInput}
                        withAsterisk
                        label={`Website name`}
                        placeholder={`website name`}
                        ariaLabel={`website name`}
                        {...form.getInputProps('websiteName')}
                    />
                    <Space h={'xl'} /> */}
            <Space h={'xl'} />
            <Checkbox
              className={styles.notifyCheckbox}
              label={`${t('notifyMeOnEmail')}`}
              checked={notifications}
              onChecked={(e) => setNotifications(e.target.checked)}
            />
            <Space h={'xl'} />
            <Checkbox
              className={styles.notifyCheckbox}
              label={`${t('redirects')}`}
              checked={redirect}
              onChecked={(e) => setRedirect(e.target.checked)}
            />
            <Flex w={'100%'} justify={'end'}>
              <Button type={'submit'} aria-label={`Save settings`} className={styles.scanBtn}>
                {t('saveSettings')}
              </Button>
            </Flex>
          </form>
        )}
      </Box>
    </>
  );
};

export default SettingsPage;
